import { API } from './interceptor'

/**
 * Crow’s Nest north-star metrics.
 * @param {{ fromDate?: string, toDate?: string, granularity?: string }} [queryParams]
 */
export async function fetchCrowsNestNorthStar(queryParams = {}) {
  const response = await API.get(
    'api2',
    'growth-engine/crows-nest/north-star',
    {
      queryStringParameters: queryParams,
    },
  )

  if (!response?.success) {
    throw new Error(response?.message || 'Failed to load north star metrics')
  }

  return {
    definition: response.definition || null,
    range: response.range || null,
    totals: response.totals || {
      opportunities: 0,
      uniqueVenues: 0,
      emailsSent: 0,
      uniqueVenuesWithEmail: 0,
    },
    series: Array.isArray(response.series) ? response.series : [],
  }
}

/**
 * Crow’s Nest opportunity feed (paginated).
 * @param {Record<string, string|number|boolean|undefined>} [queryParams]
 */
export async function fetchCrowsNestOpportunities(queryParams = {}) {
  const response = await API.get(
    'api2',
    'growth-engine/crows-nest/opportunities',
    {
      queryStringParameters: queryParams,
    },
  )

  if (!response?.success) {
    throw new Error(response?.message || 'Failed to load opportunity feed')
  }

  return {
    range: response.range || null,
    filters: response.filters || {},
    totals: response.totals || null,
    opportunities: Array.isArray(response.opportunities)
      ? response.opportunities
      : [],
    pagination: response.pagination || {
      page: 1,
      pageSize: 25,
      total: 0,
      totalPages: 0,
      hasMore: false,
    },
  }
}

/**
 * Crow’s Nest paywall vs supplier-add totals (unfiltered by type).
 * @param {Record<string, string|number|boolean|undefined>} [queryParams]
 */
export async function fetchCrowsNestOpportunityTotals(queryParams = {}) {
  const response = await API.get(
    'api2',
    'growth-engine/crows-nest/opportunity-totals',
    {
      queryStringParameters: queryParams,
    },
  )

  if (!response?.success) {
    throw new Error(response?.message || 'Failed to load opportunity totals')
  }

  return {
    definition: response.definition || null,
    range: response.range || null,
    filters: response.filters || {},
    totals: response.totals || {
      opportunities: 0,
      paywallLimit: 0,
      supplierAdded: 0,
      supplierMilestone: 0,
      byType: {
        paywall_limit: 0,
        supplier_added: 0,
        supplier_milestone: 0,
      },
    },
  }
}

/**
 * Patch Crow’s Nest opportunity sales status / note.
 * @param {string} opportunityId
 * @param {{ status?: string, salesNote?: string }} body
 */
export async function patchCrowsNestOpportunity(opportunityId, body = {}) {
  const response = await API.patch(
    'api2',
    `growth-engine/crows-nest/opportunities/${opportunityId}`,
    { body },
  )

  if (!response?.success) {
    throw new Error(response?.message || 'Failed to update opportunity')
  }

  return {
    opportunity: response.opportunity || null,
  }
}

/**
 * Crow’s Nest funnel / per-feature summary.
 * @param {{ fromDate?: string, toDate?: string, plan?: string, featureId?: string, platform?: string }} [queryParams]
 */
export async function fetchCrowsNestFunnel(queryParams = {}) {
  const response = await API.get('api2', 'growth-engine/crows-nest/funnel', {
    queryStringParameters: queryParams,
  })

  if (!response?.success) {
    throw new Error(response?.message || 'Failed to load funnel summary')
  }

  return {
    definition: response.definition || null,
    range: response.range || null,
    filters: response.filters || {},
    totals: response.totals || {
      views: 0,
      clicks: 0,
      opened: 0,
      firstUses: 0,
      firstSuccess: 0,
      repeatUse: 0,
      paywalls: 0,
      rates: {},
    },
    byFeature: Array.isArray(response.byFeature) ? response.byFeature : [],
  }
}

/**
 * Crow’s Nest ladder census — “6 of how many.”
 * @param {{ plan?: string }} [queryParams]
 */
export async function fetchCrowsNestLadderCensus(queryParams = {}) {
  const response = await API.get(
    'api2',
    'growth-engine/crows-nest/ladder-census',
    {
      queryStringParameters: queryParams,
    },
  )

  if (!response?.success) {
    throw new Error(response?.message || 'Failed to load ladder census')
  }

  return {
    definition: response.definition || null,
    filters: response.filters || {},
    asOf: response.asOf || null,
    timezone: response.timezone || null,
    totals: response.totals || {
      venues: 0,
      dormant: 0,
      active: 0,
      suppliers: {},
      unadopted: {},
      shares: {},
    },
  }
}

/**
 * Crow’s Nest time to first success / paywall.
 * @param {{ fromDate?: string, toDate?: string, plan?: string, featureId?: string, platform?: string }} [queryParams]
 */
export async function fetchCrowsNestTimeToConversion(queryParams = {}) {
  const response = await API.get(
    'api2',
    'growth-engine/crows-nest/time-to-conversion',
    {
      queryStringParameters: queryParams,
    },
  )

  if (!response?.success) {
    throw new Error(response?.message || 'Failed to load time to conversion')
  }

  return {
    definition: response.definition || null,
    range: response.range || null,
    filters: response.filters || {},
    totals: response.totals || {
      timeToFirstSuccess: { medianDays: null, sampleSize: 0 },
      timeToPaywall: { medianDays: null, sampleSize: 0 },
    },
    byFeature: Array.isArray(response.byFeature) ? response.byFeature : [],
  }
}

/**
 * Crow’s Nest unadopted venues — Free and paid, recent orders, depth gaps.
 * @param {{ plan?: string, feature?: string, page?: number, pageSize?: number }} [queryParams]
 */
export async function fetchCrowsNestUnadoptedVenues(queryParams = {}) {
  const response = await API.get('api2', 'growth-engine/crows-nest/unadopted', {
    queryStringParameters: queryParams,
  })

  if (!response?.success) {
    throw new Error(response?.message || 'Failed to load unadopted venues')
  }

  return {
    definition: response.definition || null,
    filters: response.filters || {},
    asOf: response.asOf || null,
    timezone: response.timezone || null,
    totals: response.totals || {
      venues: 0,
      paid: 0,
      free: 0,
      byFeature: {},
      byPlan: {},
    },
    venues: Array.isArray(response.venues) ? response.venues : [],
    pagination: response.pagination || {
      page: 1,
      pageSize: 25,
      total: 0,
      totalPages: 0,
      hasMore: false,
    },
  }
}

/**
 * Crow’s Nest Ollie findings by check — found, shown, fixed, time to fix,
 * reported wrong.
 * @param {{ fromDate?: string, toDate?: string, platform?: string }} [queryParams]
 */
export async function fetchCrowsNestOllieFindings(queryParams = {}) {
  const response = await API.get(
    'api2',
    'growth-engine/crows-nest/ollie-findings',
    {
      queryStringParameters: queryParams,
    },
  )

  if (!response?.success) {
    throw new Error(response?.message || 'Failed to load Ollie findings')
  }

  return {
    definition: response.definition || null,
    range: response.range || null,
    filters: response.filters || {},
    totals: response.totals || null,
    byCheck: Array.isArray(response.byCheck) ? response.byCheck : [],
  }
}
